import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import api from '../utils/api';
import { FiClock, FiUsers, FiDownload, FiX } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';

const CompanyOverviewCharts = ({ userId }) => {
    const [trendData, setTrendData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState(new Date().getFullYear());

    // Drill-down states
    const [showModal, setShowModal] = useState(false);
    const [details, setDetails] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [modalInfo, setModalInfo] = useState({ title: '', month: '' });
    const [activeTrendType, setActiveTrendType] = useState('total');

    const fetchTrendData = async () => {
        try {
            setLoading(true);
            const res = await api.get('/hrms/company-overview-trends', {
                params: { year, user_id: userId }
            });
            if (res.data.status) {
                setTrendData(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching trend data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRecordDetails = async (monthIndex, type) => {
        try {
            const month = monthIndex + 1;
            const monthName = trendData.months[monthIndex];
            const typeLabels = {
                'total': 'Total Leaves',
                'unscheduled': 'Unscheduled Leaves',
                'scheduled': 'Scheduled Leaves',
                'permission': 'Permissions'
            };

            setModalInfo({ title: typeLabels[type], month: monthName });
            setActiveTrendType(type);
            setDetailsLoading(true);
            setShowModal(true);

            const res = await api.get('/hrms/trend-record-details', {
                params: { year, month, type, user_id: userId }
            });

            if (res.data.status) {
                setDetails(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching record details:', error);
            toast.error('Failed to load details');
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchTrendData();
    }, [year, userId]);

    const exportToExcel = async () => {
        if (!details.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Leave Details');

        // 1. Report Title
        const titleRow = worksheet.addRow(['ORGANIZATIONAL LEAVE TRENDS REPORT']);
        worksheet.mergeCells(`A${titleRow.number}:E${titleRow.number}`);
        titleRow.font = { name: 'Arial Black', size: 16, color: { argb: 'FFFFFFFF' } };
        titleRow.alignment = { vertical: 'middle', horizontal: 'center' };
        titleRow.getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1F2937' } // Dark Gray
        };

        // 2. Metadata
        const metaData = [
            [`Report Type: ${modalInfo.title}`],
            [`Analysis Period: ${modalInfo.month} ${year}`],
            [`Generated On: ${new Date().toLocaleString()}`],
            [] // Spacer
        ];

        metaData.forEach(data => {
            const row = worksheet.addRow(data);
            worksheet.mergeCells(`A${row.number}:E${row.number}`);
            if (data.length > 0) {
                row.font = { bold: true };
            }
        });

        // 3. Table Headers
        const headerRow = worksheet.addRow(["EMPLOYEE NAME", "LEAVE DATE", "LEAVE TYPE", activeTrendType === 'permission' ? "HOURS" : "DURATION", "REASON"]);
        headerRow.height = 25;

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1F2937' } // Matching Title Dark Gray
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // 4. Data Rows
        details.forEach(d => {
            const row = worksheet.addRow([
                d.employee_name,
                d.leave_date,
                d.type,
                d.duration,
                d.reason || 'N/A'
            ]);

            // Apply styling to each cell in the row (Columns 1-5)
            for (let i = 1; i <= 5; i++) {
                const cell = row.getCell(i);
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                // Alignment logic
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: (i === 2 || i === 3 || i === 4) ? 'center' : 'left',
                    wrapText: i === 5 // Enable wrap for Reason column
                };

                cell.font = { size: 10 };
            }
        });

        worksheet.columns = [
            { width: 30 }, // Employee
            { width: 20 }, // Date
            { width: 20 }, // Type
            { width: 12 }, // Duration
            { width: 60 }  // Reason (Increased width)
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Leave_Trends_${modalInfo.month}_${year}.xlsx`);
    };

    if (loading && !trendData) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
            </div>
        );
    }

    const isCurrentYear = year === new Date().getFullYear();
    const displayLimit = isCurrentYear ? trendData.summary.current_month : 12;
    const categories = trendData.months.slice(0, displayLimit);

    const sliceData = (arr) => (arr || []).slice(0, displayLimit);

    const commonOptions = {
        credits: { enabled: false },
        title: { text: null },
        xAxis: {
            categories: categories,
            gridLineWidth: 0,
            lineWidth: 0,
            tickWidth: 0,
            labels: { style: { color: '#94a3b8', fontSize: '10px' } }
        },
        yAxis: {
            title: { text: null },
            gridLineColor: '#f1f5f9',
            labels: { style: { color: '#94a3b8', fontSize: '10px' } }
        },
        legend: {
            align: 'left',
            verticalAlign: 'top',
            itemStyle: { fontSize: '11px', fontWeight: '500', color: '#64748b' },
            symbolRadius: 4
        },
        tooltip: {
            shared: true,
            backgroundColor: '#ffffff',
            borderRadius: 12,
            borderWidth: 1,
            borderColor: '#f1f5f9',
            style: { fontSize: '12px' },
            shadow: { color: 'rgba(0,0,0,0.05)', offsetX: 0, offsetY: 4, opacity: 0.1, width: 12 }
        },
        plotOptions: {
            series: {
                cursor: 'pointer',
                marker: { enabled: true, radius: 4, states: { hover: { enabled: true, radius: 6 } } },
                point: {
                    events: {
                        click: function () {
                            const type = this.series.name === 'Permissions' ? 'permission' :
                                (this.series.name === 'Unscheduled' || this.series.name === 'Unscheduled Leaves' ? 'unscheduled' :
                                    (this.series.name === 'Scheduled' || this.series.name === 'Scheduled Leaves' ? 'scheduled' : 'total'));
                            fetchRecordDetails(this.x, type);
                        }
                    }
                }
            }
        }
    };

    const cardStyle = "bg-white p-6 rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.05)] border border-gray-50 flex flex-col";

    return (
        <div className="w-full bg-[#f8fafc] py-8 px-4 sm:px-6">
            <div className="max-w-[1600px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-6 border-b border-gray-100">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Organizational Leave Trends Overview</h2>
                        <p className="text-sm text-gray-500 mt-1">Cross-organizational attendance patterns</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative group/select">
                            <select
                                value={year}
                                onChange={(e) => setYear(parseInt(e.target.value))}
                                className="text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl pl-4 pr-10 py-2.5 outline-none focus:ring-2 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 min-w-[80px]"
                                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                            >
                                {[0, 1, 2].map(i => {
                                    const y = new Date().getFullYear() - i;
                                    return <option key={y} value={y}>{y}</option>
                                })}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover/select:text-red-500 transition-colors">
                                {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"> */}
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                                {/* </svg> */}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Total Leaves Trend */}
                    <div className={cardStyle}>
                        <h4 className="text-sm font-bold text-gray-700 mb-4">Total Leaves Trend</h4>
                        <div className="flex-1 min-h-[250px]">
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    ...commonOptions,
                                    chart: { type: 'line', height: 250, backgroundColor: 'transparent' },
                                    series: [
                                        {
                                            name: 'Total Leaves',
                                            data: sliceData(trendData?.total_leaves),
                                            color: '#ef4444',
                                            lineWidth: 3,
                                            marker: { symbol: 'circle' }
                                        },
                                        {
                                            name: 'Unscheduled Leaves',
                                            data: sliceData(trendData?.unscheduled_leaves),
                                            color: '#F97316',
                                            lineWidth: 3,
                                            dashStyle: 'ShortDash',
                                            marker: { symbol: 'diamond' }
                                        }
                                    ]
                                }}
                            />
                        </div>
                        <div className="mt-4 flex flex-col gap-1 border-t border-gray-50 pt-4">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Current Month: {trendData?.total_leaves[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                                <span className="text-xs font-semibold text-gray-400">Last Month: {trendData?.total_leaves[trendData.summary.last_month - 1] || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* Unscheduled Leaves Trend */}
                    <div className={cardStyle}>
                        <h4 className="text-sm font-bold text-gray-700 mb-4">Unscheduled & Scheduled Analysis</h4>
                        <div className="flex-1 min-h-[250px]">
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    ...commonOptions,
                                    chart: { type: 'column', height: 250, backgroundColor: 'transparent' },
                                    plotOptions: {
                                        ...commonOptions.plotOptions,
                                        column: {
                                            pointPadding: 0.2,
                                            borderWidth: 0,
                                            borderRadius: 4
                                        }
                                    },
                                    series: [
                                        {
                                            name: 'Scheduled',
                                            data: sliceData(trendData?.scheduled_leaves),
                                            color: '#3B82F6',
                                        },
                                        {
                                            name: 'Unscheduled',
                                            data: sliceData(trendData?.unscheduled_leaves),
                                            color: '#F97316',
                                        }
                                    ]
                                }}
                            />
                        </div>
                        <div className="mt-4 flex flex-col gap-1 border-t border-gray-50 pt-4">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Scheduled (CM): {trendData?.scheduled_leaves[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Unscheduled (CM): {trendData?.unscheduled_leaves[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* Total Permissions Trend */}
                    <div className={cardStyle}>
                        <h4 className="text-sm font-bold text-gray-700 mb-4">Total Permissions Trend</h4>
                        <div className="flex-1 min-h-[250px]">
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    ...commonOptions,
                                    chart: { type: 'area', height: 250, backgroundColor: 'transparent' },
                                    series: [{
                                        name: 'Permissions',
                                        data: sliceData(trendData?.permissions),
                                        color: '#22C55E',
                                        fillColor: {
                                            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                                            stops: [
                                                [0, 'rgba(34, 197, 94, 0.2)'],
                                                [1, 'rgba(34, 197, 94, 0)']
                                            ]
                                        },
                                        lineWidth: 3,
                                        marker: { symbol: 'circle' }
                                    }]
                                }}
                            />
                        </div>
                        <div className="mt-4 flex flex-col gap-1 border-t border-gray-50 pt-4">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                <span className="text-xs font-semibold text-gray-600">Current Month: {trendData?.permissions[trendData.summary.current_month - 1] || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                                <span className="text-xs font-semibold text-gray-400">Last Month: {trendData?.permissions[trendData.summary.last_month - 1] || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Drill-down Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999] p-2 sm:p-4">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
                            {/* Modal Header */}
                            <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white sticky top-0">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shadow-inner shrink-0">
                                        <FiUsers className="text-lg sm:text-xl" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-base sm:text-xl font-black text-gray-800 tracking-tight truncate">{modalInfo.title} Details</h3>
                                        <p className="text-[10px] sm:text-xs text-gray-400 font-medium tracking-wide">Analysis for <span className="text-red-600 font-bold uppercase">{modalInfo.month} {year}</span></p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={exportToExcel}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-[#047857] hover:bg-[#064e3b] text-white rounded-lg transition-all text-[11px] font-bold shadow-md active:scale-95"
                                    >
                                        <FiDownload className="text-sm" />
                                        <span>Export</span>
                                    </button>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all border border-transparent hover:border-gray-200"
                                    >
                                        <FiX size={18} className="sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/30">
                                {detailsLoading ? (
                                    <div className="flex flex-col items-center justify-center py-12 gap-5">
                                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500/20 border-b-red-500"></div>
                                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Loading Records...</p>
                                    </div>
                                ) : details.length > 0 ? (
                                    <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-xl">
                                        <table className="w-full text-left border-collapse min-w-[500px]">
                                            <thead>
                                                <tr className="bg-gray-50/80">
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Employee</th>
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Date Range</th>
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Leave Type</th>
                                                    <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">{activeTrendType === 'permission' ? 'Hours' : 'Days'}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {details.map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-red-50/40 transition-all duration-300 group">
                                                        <td className="py-3 px-6">
                                                            <span className="font-bold text-gray-800 group-hover:text-red-700 transition-colors text-xs sm:text-[14px]">{row.employee_name}</span>
                                                        </td>
                                                        <td className="py-3 px-6 text-[10px] sm:text-[12px] text-gray-600 font-semibold whitespace-nowrap">
                                                            {row.leave_date}
                                                        </td>
                                                        <td className="py-3 px-6">
                                                            <span className="px-3 py-1 bg-gray-100/80 text-[9px] font-black uppercase rounded text-gray-500 whitespace-nowrap border border-gray-200/50">
                                                                {row.type}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-6 text-center">
                                                            <span className="inline-block px-3 py-1 bg-red-50 text-red-600 rounded-lg font-black text-[10px] shadow-sm shadow-red-100/50 border border-red-100/50">
                                                                {activeTrendType === 'permission' ? row.duration : parseFloat(row.duration).toFixed(1)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 sm:py-24 text-gray-300 bg-white rounded-2xl sm:rounded-3xl border border-dashed border-gray-200">
                                        <FiUsers className="mb-4 sm:mb-6 opacity-20 text-4xl sm:text-6xl" />
                                        <p className="font-black text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 text-center">No Records Found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyOverviewCharts;
